/**
 * One scope's `billing.directPrices` value: product key → price in major units.
 *
 * The api's definition is `z.record(z.string(), MoneyAmountSchema)`, and the
 * admin never widens that — a map is what is stored and a map is what is sent.
 */
export type DirectPriceMap = Readonly<Record<string, number>>;

/**
 * A stored `billing.directPrices` value, made safe to render.
 *
 * The setting is operator-writable jsonb and `SettingValueDto.value` is
 * `unknown`, so every read of it happens inside a render where a throw is a
 * blank screen. Anything that is not a plain object degrades to no prices, and
 * an individual entry the api would refuse — a non-number, a negative, a blank
 * key — is dropped rather than drawn: an editable box holding a value that
 * cannot be saved is a Save that 422s on a number the operator never typed.
 */
export function priceMapOf(value: unknown): DirectPriceMap {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }

  const map: Record<string, number> = {};

  for (const [key, amount] of Object.entries(value)) {
    if (key.trim() !== '' && typeof amount === 'number' && Number.isFinite(amount) && amount >= 0) {
      map[key] = amount;
    }
  }

  return map;
}

/**
 * The priced product keys, sorted.
 *
 * Sorted rather than left in insertion order because `jsonb` does not preserve
 * key order — the same trap `product-keys.util` records against this very
 * setting: an unsorted render reshuffles every time the map is written.
 */
export function pricedKeysOf(map: DirectPriceMap): string[] {
  return Object.keys(map).sort();
}

/** One product priced, with every other key in the scope left exactly as it was. */
export function withPrice(map: DirectPriceMap, productKey: string, amount: number): DirectPriceMap {
  return { ...map, [productKey]: amount };
}

/**
 * One product unpriced.
 *
 * The caller reads the result's size to choose between a write and a CLEAR:
 * storing `{}` records "overridden with nothing", where the truth the operator
 * asked for is "not overridden at all" (`useAiPricing`'s rule).
 */
export function withoutPrice(map: DirectPriceMap, productKey: string): DirectPriceMap {
  const next = { ...map };

  delete next[productKey];

  return next;
}
